import React, { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';
import Header from '../components/header/Header.jsx';
import Footer from '../components/footer/Footer.jsx';
import FixedPlugin from '../components/fixedPlugin/FixedPlugin';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession } from '../services/paymentService';

const PLANS = [
	{
		id: 'free-trial',
		planKey: 'small',
		trial: true,
		name: 'Free Trial',
		tagline: '24-Hour Access',
		users: '1 User',
		monthly: 0,
		annual: null,
		cta: 'Start Free Trial',
		features: [
			'Full access to core features',
			'Create interim valuations',
			'Generate payment applications',
			'AI-assisted payment calculations',
			'Project dashboard access',
			'No commitment',
		],
	},
	{
		id: 'small-company',
		planKey: 'small',
		name: 'Small Company',
		tagline: 'Designed for Small Contractors and Consultancies',
		users: 'Up to 5 Users',
		monthly: 4.99,
		annual: 49.99,
		cta: 'Choose Small Company',
		features: [
			'Up to 5 user accounts',
			'Unlimited project creation',
			'Interim valuation generation',
			'Payment applications',
			'Payment notices and pay less notices',
			'AI-assisted calculations',
			'Project dashboard',
			'Secure cloud storage',
			'Standard support',
		],
	},
	{
		id: 'medium-enterprise',
		planKey: 'medium',
		name: 'Medium Company',
		tagline: 'For Growing Construction SMEs',
		users: 'Up to 25 Users',
		monthly: 9.99,
		annual: 99.99,
		cta: 'Choose Medium Company',
		mostPopular: true,
		features: [
			'Up to 25 user accounts',
			'Everything in Small Company',
			'Multi-project portfolio management',
			'Enhanced reporting dashboard',
			'Team collaboration features',
			'Advanced project analytics',
			'Priority support',
			'Organisation-wide administration controls',
		],
	},
	{
		id: 'enterprise',
		planKey: 'enterprise',
		name: 'Enterprise Company',
		tagline: 'For Large Contractors, Developers and Multi-Office Organisations',
		users: 'Unlimited Users',
		monthly: 14.99,
		annual: 149.99,
		cta: 'Choose Enterprise Company',
		features: [
			'Unlimited users',
			'Everything in Medium Company',
			'Enterprise-level administration',
			'Advanced analytics and business intelligence',
			'Multi-region project management',
			'API access and integrations',
			'Dedicated onboarding support',
			'Priority feature access',
			'Premium support',
		],
	},
];

const savingsPercent = (monthly, annual) => {
	if (!annual) return 0;
	return Math.round((1 - annual / (monthly * 12)) * 100);
};

const Pricing = () => {
	const [billing, setBilling] = useState('monthly');
	const [loadingPlan, setLoadingPlan] = useState(null);
	const { isAuthenticated, userProfile } = useAuth();
	const toast = useToast();

	const handleChoose = async (plan) => {
		if (!isAuthenticated || !userProfile) {
			toast({
				title: 'Sign in required',
				description: 'Please sign in to choose a plan.',
				status: 'warning',
				duration: 4000,
				isClosable: true,
			});
			return;
		}

		setLoadingPlan(plan.id);
		try {
			const billingPeriod = plan.trial ? 'monthly' : billing;
			const companyId = userProfile.PK?.replace('COMPANY#', '');
			const { url } = await createCheckoutSession(plan.planKey, billingPeriod, userProfile.email, userProfile.userId, companyId, !!plan.trial);
			window.location.href = url;
		} catch (err) {
			toast({
				title: 'Checkout error',
				description: err.message || 'Could not initiate checkout. Please try again.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setLoadingPlan(null);
		}
	};

	return (
		<div className='min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white'>
			<FixedPlugin />
			<Header />

			<main className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
				<div className='text-center mb-10'>
					<h1 className='text-4xl font-bold mb-3'>Pricing Plans</h1>
					<p className='text-lg text-gray-700 dark:text-gray-300'>
						Simple, affordable pricing for every construction business.
					</p>
				</div>

				<div className='flex justify-center items-center gap-3 mb-12'>
					<span className={`text-sm font-medium ${billing === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
						Monthly
					</span>
					<button
						type='button'
						role='switch'
						aria-checked={billing === 'annual'}
						onClick={() => setBilling(billing === 'monthly' ? 'annual' : 'monthly')}
						className='relative inline-flex h-7 w-12 items-center rounded-full bg-teal-500 transition-colors'
					>
						<span
							className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
								billing === 'annual' ? 'translate-x-6' : 'translate-x-1'
							}`}
						/>
					</button>
					<span className={`text-sm font-medium ${billing === 'annual' ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
						Annual
					</span>
					<span className='ml-1 rounded-full bg-green-100 dark:bg-green-900/40 px-2.5 py-1 text-xs font-semibold text-green-700 dark:text-green-400'>
						Save ~17%
					</span>
				</div>

				<div className='grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4 items-start'>
					{PLANS.map((plan) => {
						const isAnnual = billing === 'annual' && plan.annual;
						const price = isAnnual ? plan.annual : plan.monthly;
						const priceLabel = plan.id === 'free-trial' ? '£0' : `£${price.toFixed(2)}`;
						const period = plan.id === 'free-trial' ? '' : isAnnual ? '/year' : '/month';
						const savings = savingsPercent(plan.monthly, plan.annual);

						return (
							<div
								key={plan.id}
								className={`relative flex flex-col rounded-2xl border bg-white dark:bg-gray-800/50 p-6 shadow-lg ${
									plan.mostPopular
										? 'border-amber-400 dark:border-amber-500 shadow-amber-200/50 dark:shadow-none'
										: 'border-gray-200 dark:border-gray-700'
								}`}
							>
								{plan.mostPopular && (
									<div className='absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-400 px-4 py-1 text-xs font-bold uppercase tracking-wide text-gray-900'>
										Most Popular
									</div>
								)}

								<h2 className='text-xl font-bold mt-2'>{plan.name}</h2>
								<p className='mt-1 text-sm text-gray-600 dark:text-gray-400 min-h-10'>{plan.tagline}</p>
								<p className='mt-2 text-xs font-semibold uppercase tracking-wide text-teal-600 dark:text-teal-400'>
									{plan.users}
								</p>

								<div className='mt-6 flex items-baseline gap-1'>
									<span className='text-4xl font-bold'>{priceLabel}</span>
									{period && <span className='text-gray-500 dark:text-gray-400'>{period}</span>}
								</div>
								{isAnnual && savings > 0 && (
									<span className='mt-2 inline-block w-fit rounded-full bg-green-100 dark:bg-green-900/40 px-2.5 py-1 text-xs font-semibold text-green-700 dark:text-green-400'>
										Save {savings}%
									</span>
								)}

								<button
									type='button'
									disabled={loadingPlan === plan.id}
									onClick={() => handleChoose(plan)}
									className='mt-6 w-full rounded-xl bg-teal-500 py-3 text-sm font-bold text-white transition hover:bg-teal-600 disabled:opacity-60'
								>
									{loadingPlan === plan.id ? 'Redirecting...' : plan.cta}
								</button>

								<ul className='mt-6 space-y-3 text-sm text-gray-700 dark:text-gray-300'>
									{plan.features.map((feature) => (
										<li key={feature} className='flex items-start gap-2'>
											<FiCheck className='mt-0.5 h-4 w-4 flex-shrink-0 text-teal-500' />
											<span>{feature}</span>
										</li>
									))}
								</ul>
							</div>
						);
					})}
				</div>
			</main>

			<Footer />
		</div>
	);
};

export default Pricing;
